import { Component, DestroyRef, Inject, OnDestroy, Optional, ChangeDetectionStrategy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CurrencyPipe, AsyncPipe, formatCurrency } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogModule, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import dayjs from 'dayjs';
import { Subscription, forkJoin, interval, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import Swal, { SweetAlertOptions } from 'sweetalert2';

import { ValuesService } from '@app/common/values/values.service';
import { SalesService } from '@app/sales/sales.service';
import { AlertService } from '@app/shared/alert.service';
import { ServerStatusService } from '@app/common/server-status.service';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { OfflineBannerComponent } from '@app/shared/offline-banner/offline-banner.component';
import { SpinnerService } from '@app/shared/spinner.service';
import { ApiResponse } from '@app/shared/_models/common';
import { SafePeriod, periodHardLimit, periodLimitLabel, periodLimitPhrase } from '@app/shared/_models/sales';
import { parseTz } from '@app/shared/time';
import { C_PAYMENT_METHOD } from '@app/shared/constants';
import { C_value } from '@app/shared/_models/common';
import { LoginService } from '@app/common/login.service';
import { CashFamily, ClosingFloat, PeriodRecap, StationBalance, cashFamilyOf, closingGap, isPosStation, mergeCashTotals, nightTransfers, nonNegativeCents, refusalText } from '@app/sales/reglement/reglement.models';
import { ReglementService } from '@app/sales/reglement/reglement.service';
import { openCashCount } from '@app/sales/reglement/cash-count-dialog/cash-count-dialog.component';
import { CentsInputDirective } from '@app/sales/reglement/cents-input.directive';
import { CashCounts } from '@app/sales/reglement/cash-count-grid/cash-count-grid.component';
import { OpenFloatData, OpenFloatDialogComponent } from './open-float-dialog/open-float-dialog.component';
import { CLOSE_WARNING, ZCloseDialogComponent, ZCloseDialogData, ZCloseResult } from './z-close-dialog/z-close-dialog.component';

// Message du voile par type de période (0 = année, 1 = mois, 2 = session journalière).
const OPEN_MSG = ['Initialisation de l\'exercice…', 'Ouverture du mois fiscal…', 'Ouverture de la session de caisse…'];
const CLOSE_MSG = ['Clôture de l\'exercice…', 'Clôture du mois fiscal…', 'Fermeture de la caisse (Z)…'];
// Poste sans tiroir-caisse : sa journée n'est pas une caisse, le voile ne le dit pas (JP 04/10).
const openMsg = (typ: number, hasDrawer: boolean) => typ === 2 && !hasDrawer ? 'Ouverture de la journée…' : OPEN_MSG[typ];
const closeMsg = (typ: number, hasDrawer: boolean) => typ === 2 && !hasDrawer ? 'Clôture de la journée (Z)…' : CLOSE_MSG[typ];

// Même format que les montants de la carte (pipe currency fr-FR) : virgule décimale.
const eur = (v: number) => formatCurrency(v, 'fr-FR', '€', 'EUR', '1.2-2');
const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

const CLOSE_WARNING_HTML = `<p style="margin:0 0 12px">${CLOSE_WARNING}</p>`;

// Corps HTML du récapitulatif (Z et lecture seule) : la Swal vit hors du composant, d'où les styles en ligne.
// Famille espèces (Espèces + Rendu monnaie) en une ligne comme dans l'onglet Règlement ; sans c_values 7, lignes du serveur telles quelles.
function recapBodyHtml(r: PeriodRecap, family: CashFamily | null, hasDrawer: boolean): string {
  const td = 'padding:2px 8px;border-bottom:1px solid #f1f5f9;text-align:left';
  const num = (v?: number) => 'padding:2px 8px;border-bottom:1px solid #f1f5f9;text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums' + ((v ?? 0) < 0 ? ';color:#dc2626' : '');
  const methods = mergeCashTotals(r.methods, family);
  const rows = methods.length
    ? methods.map((m) => `<tr><td style="${td}">${esc(m.name ?? 'Mode ' + m.method)}</td><td style="${num()}">${m.count}</td><td style="${num(m.amount)}">${eur(m.amount)}</td></tr>`).join('')
    : `<tr><td colspan="3" style="${td};text-align:center;color:#94a3b8">Aucun règlement encaissé à ce poste</td></tr>`;
  const count = r.methods.reduce((n, m) => n + m.count, 0);
  const fond = r.opening_float_found ? `Fond de caisse ${eur(r.opening_float)}` : `Fond de caisse <b>inconnu</b> (${eur(r.opening_float)} retenu)`;
  // Poste sans tiroir-caisse : rien à compter, l'encaissé par mode seul.
  const drawerLine = hasDrawer
    ? `<p style="margin:12px 0 0;line-height:1.6">${fond} + Espèces encaissées ${eur(r.cash_payments)} + Reçu ${eur(r.transfers_in)} − Versé au coffre ${eur(r.transfers_out)}`
      + ` = <b>Espèces attendues dans la caisse ${eur(r.expected_cash)}</b></p>`
    : '';
  return `<div style="text-align:left;font-size:14px">`
    + `<div style="margin-bottom:4px;font-weight:500">${esc(r.station_name)} — encaissé par mode</div>`
    + `<table style="width:100%;border-collapse:collapse"><thead><tr><th style="${td}">Mode</th><th style="${num()}">Nombre</th><th style="${num()}">Montant</th></tr></thead>`
    + `<tbody>${rows}</tbody>`
    + `<tfoot><tr><th style="${td}">Total</th><th style="${num()}">${count}</th><th style="${num(r.total)}">${eur(r.total)}</th></tr></tfoot></table>`
    + drawerLine + `</div>`;
}

const at = (v: string | null | undefined) => parseTz(v)?.format('DD/MM/YYYY HH:mm') ?? '';

// Lecture seule : la fenêtre de la session à la place de l'avertissement de clôture ; session close, le fond compté au Z et son écart.
export function sessionRecapHtml(r: PeriodRecap, family: CashFamily | null = null, hasDrawer = true): string {
  const span = r.status === 'open' ? `Session ouverte depuis le ${at(r.start_date)}, comptée jusqu'au ${at(r.end_at)}` : `Session clôturée : du ${at(r.start_date)} au ${at(r.end_at)}`;
  // Écart contre l'attendu figé au Z, tel que le caissier l'a vu : un règlement tardif ne le change plus.
  const gap = r.closing_float != null && r.closing_expected != null ? closingGap({ expected_cash: r.closing_expected }, 0, r.closing_float) : 0;
  const closing = hasDrawer && r.closing_float != null
    ? `<p style="margin:4px 0 0;text-align:left;font-size:14px">Fond du soir compté <b>${eur(r.closing_float)}</b> — écart <b style="${gap ? 'color:#dc2626' : ''}">${eur(gap)}</b></p>`
    : '';
  return `<p style="margin:0 0 12px;color:#475569">${esc(span)}</p>` + recapBodyHtml(r, family, hasDrawer) + closing;
}

/**
 * Periods Component
 *
 * Manages the display and lifecycle of sales periods (year, month, day).
 * Handles period opening, closing, and tracking soft/hard expiration limits based on backend configurations.
 * 
 * @usageNotes
 * - Called via routing at path: `sales/periods`.
 * - Prepared to be opened as a dialog (via `MatDialogRef`).
 * 
 * @dependencies
 * - Components: `MenuCommonComponent`
 * - Services: `SalesService`, `AlertService`, `ValuesService`
 * - Models: `ApiResponse`, `SafePeriod`
 */
@Component({
  selector: 'app-periods',
  templateUrl: './periods.component.html',
  styleUrls: ['./periods.component.scss', /* '../../styles-custom.scss'*/],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MenuCommonComponent, MatDialogModule, MatDialogActions, MatDialogContent, MatIconModule, MatDialogTitle, MatDialogClose, MatButtonModule, AsyncPipe, CurrencyPipe, MatFormFieldModule, MatInputModule, ReactiveFormsModule, OfflineBannerComponent, CentsInputDirective]
})

export class PeriodsComponent implements OnDestroy {
  /** Array of all currently loaded periods */
  public periods: Array<SafePeriod> = [];
  /** Reference to the year period if available */
  public yearPeriod?: SafePeriod;
  /** Reference to the month period if available */
  public monthPeriod?: SafePeriod;
  /** Reference to the day period if available */
  public dayPeriod?: SafePeriod;
  /** Indicates if period data was successfully loaded */
  public dataOk: boolean = false;
  /** Indicates if an error occurred during data loading */
  public dataError: boolean = false;
  /** Indicates if the station is in a commissioning state */
  public isCommissioning: boolean = false;
  /** Message explaining the commissioning state */
  public commissioningMsg: string = '';
  /** Form control for the initial cash in amount when enrolling a station */
  public cashInControl = new FormControl<number | null>(null, nonNegativeCents);
  // Dernier comptage pièce à pièce du fond de mise en service : rouvrir la grille reprend où l'on en était ; son total signale une retouche à la main.
  private openingCounts: CashCounts | undefined;
  public enrollCountTotal: number | null = null;
  /** Current start of the day reference */
  public now = dayjs().startOf('day');

  // BOFIP UX Compliance:
  // The server calculates a theoretical 'end_date' (Rollover Time) and a strict 'z_report_hard_limit_date' (end_date + min(legal 24h, c_values 34 `z_report_soft_grace_hours`)).
  // The client uses these flags strictly for UX purposes (displaying warnings and blocks),
  // but the ultimate enforcement always occurs on the Rust backend.
  /** Indicates the rollover time has passed (UX soft warning). */
  public isSoftExpired: boolean = false;
  /** Indicates the hard limit is reached (UX hard block). */
  public isHardExpired: boolean = false;
  /** Ce qui est dépassé, tel que l'écran l'annonce : « Limite des 6 h » ou, sans grâce, « Heure de clôture ». */
  public hardLimitLabel: string = '';
  /** Le rappel du badge orange : ce qu'il reste à dépasser, « la limite des 6 h n'est pas dépassée ». */
  public softLimitNote: string = '';
  private timerSub?: Subscription;
  private spin = inject(SpinnerService);
  private reglementService = inject(ReglementService);
  private loginService = inject(LoginService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  /** Espèces attendues de la session journalière affichée ; null = lecture en échec ou pas encore faite (ligne absente). */
  public expectedCash: number | null = null;
  private expectedCashFor?: string;
  private expectedCashSub?: Subscription;
  private cashFamily: CashFamily | null = null;

  /** Sans tiroir-caisse, aucune espèce : ni fond à l'ouverture, ni comptage au Z, ni espèces attendues (JP 04/10). Un coffre n'a jamais de période. */
  get hasDrawer(): boolean {
    return isPosStation(this.loginService.currentstation);
  }

  constructor(private salesService: SalesService, private alertService: AlertService, private valuesService: ValuesService, public serverStatusService: ServerStatusService, @Optional() public dialogRef: MatDialogRef<PeriodsComponent>, @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any) {
    // Subscribe to server status. It will immediately emit the current status (BehaviorSubject).
    // If it's online (e.g. on component init, or recovering from an outage), we trigger load().
    // Using takeUntilDestroyed() automatically cleans this up when the component is destroyed.
    this.serverStatusService.isOnline$.pipe(
      takeUntilDestroyed()
    ).subscribe(isOnline => {
      if (isOnline) {
        this.load();
      }
    });
    // Le bouton de caisse du bandeau est aussi sur cet écran : un versement au coffre change les espèces attendues.
    this.reglementService.changed$.pipe(takeUntilDestroyed()).subscribe(() => this.loadExpectedCash());
    // Flux vivant du cache : la famille espèces est connue bien avant un Z ; en échec, le récapitulatif garde les lignes du serveur.
    this.valuesService.getValues$(C_PAYMENT_METHOD).pipe(takeUntilDestroyed()).subscribe({
      next: (values: C_value[]) => this.cashFamily = cashFamilyOf(values),
      error: (e: unknown) => console.error('[PeriodsComponent] modes de règlement indisponibles', e),
    });
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  /**
   * Cleans up the interval timer to avoid memory leaks if the component is destroyed.
   */
  private stopTimer(): void {
    if (this.timerSub) {
      this.timerSub.unsubscribe();
      this.timerSub = undefined;
    }
  }

  /**
   * Starts a background interval to constantly check the current local time against the
   * period limits provided by the server. This ensures the UI reacts in real-time when
   * a threshold (soft or hard limit) is crossed.
   */
  private startTimer(): void {
    this.stopTimer();
    this.evaluateExpirations(); // Immediate evaluation on load
    // Re-evaluate every 15 seconds to keep the UI in sync
    this.timerSub = interval(15000).subscribe(() => {
      this.evaluateExpirations();
    });
  }

  /**
   * Evaluates if the current open period has exceeded its allowed durations.
   * 
   * - Phase A: Normal Operation (now < end_date). Both flags false.
   * - Phase B: Soft Expired (end_date <= now < z_report_hard_limit_date). User should close the session.
   * - Phase C: Hard Expired (now >= z_report_hard_limit_date). Strict BOFIP violation limit.
   */
  private evaluateExpirations(): void {
    if (!this.dayPeriod || this.dayPeriod.status !== 'open') {
      this.isSoftExpired = false;
      this.isHardExpired = false;
      this.hardLimitLabel = '';
      this.softLimitNote = '';
      return;
    }
    const now = new Date().getTime();

    // Theoretical limit (e.g., 02:00 AM the next day based on DB configuration)
    const end = this.dayPeriod.end_date.getTime();

    // Absolute limit (end_date + tolerance, capped by the legal limit). Bypassing this results in server-side rejection.
    const hardLimit = periodHardLimit(this.dayPeriod.end_date, this.dayPeriod.z_report_hard_limit_date);

    // The hard limit takes precedence.
    this.isHardExpired = now >= hardLimit;
    this.hardLimitLabel = periodLimitLabel(this.dayPeriod.end_date, this.dayPeriod.z_report_hard_limit_date);
    this.softLimitNote = `${periodLimitPhrase(this.dayPeriod.end_date, this.dayPeriod.z_report_hard_limit_date)} n'est pas dépassée`;

    // The soft limit is only active if we are past the end date, but haven't reached the hard limit yet.
    this.isSoftExpired = now >= end && !this.isHardExpired;
  }

  /**
   * Loads the current status of all periods from the server.
   * Updates component state upon success or handles errors such as commissioning requirements.
   */
  load(message?: string, afterLoad?: () => void): void {
    const periods$ = this.salesService.getPeriods();
    // Relais après une écriture : ce voile se lève avant que celui du POST ne tombe, l'écran reste figé jusqu'à l'état réel.
    // `afterLoad` passe par un finalize posé APRÈS block, donc voile tombé : une Swal sous le voile (top layer) serait invisible.
    (message ? periods$.pipe(this.spin.block(message)) : periods$).pipe(finalize(() => afterLoad?.())).subscribe({
      next: (response: ApiResponse<Array<SafePeriod | null>>) => {
        console.log('response: ' + JSON.stringify(response, null, 2));
        // Rust returns Vec<Option<SafePeriod>> of size 3 inside response.data
        if (response && response.success && response.data && response.data.length === 3) {
          const data = response.data;
          this.periods = data as SafePeriod[];
          this.yearPeriod = data[0] ? new SafePeriod(data[0]) : undefined;
          this.monthPeriod = data[1] ? new SafePeriod(data[1]) : undefined;
          this.dayPeriod = data[2] ? new SafePeriod(data[2]) : undefined;
          this.dataOk = true;
          this.dataError = false;
          this.isCommissioning = false;

          // Initiate the reactive UX timer for the newly loaded day period
          this.startTimer();
          this.loadExpectedCash();
        } else if (response && !response.success && response.error_code === 1201) {
          this.isCommissioning = true;
          this.commissioningMsg = response.error_msg || response.msg || '';
          this.dataOk = true;
          this.dataError = false;
        } else {
          this.dataError = true;
        }
      },
      error: (err: HttpErrorResponse) => {
        if (err.status !== 0 && err.status !== 502 && err.status !== 503 && err.status !== 504) {
          this.dataError = true;
        }
        console.error(err);
      }
    });
  }

  /**
   * Enrolls the station by opening a new day period with the provided cash-in amount.
   */
  onEnrollStation(): void {
    const cashIn = this.hasDrawer ? this.cashInControl.value : undefined;   // poste sans tiroir-caisse : mis en service sans fond
    if (cashIn === null) return;                  // CashIn input is mandatory but can be 0
    if (this.spin.isLoading) return;              // Double clic : le voile n'est peint qu'au tour de boucle suivant

    const message = this.hasDrawer ? 'Mise en service de la caisse…' : 'Mise en service du poste…';
    this.salesService.postPeriodOpen(2, cashIn).pipe(this.spin.block(message)).subscribe({
      next: (response: ApiResponse<any>) => {
        console.log("[onEnrollStation] Server response: " + JSON.stringify(response));
        this.load(message, this.openNotice(response));
      },
      error: (error: HttpErrorResponse) => {
        console.error(error);
        this.alertService.error(error.message + "\n" + error.error);
      }
    });
  }

  /**
   * Requests the server to close a specific period.
   *
   * @param typ The type identifier of the period to close (0 = Year, 1 = Month, 2 = Day).
   */
  onClosePeriod(typ: number): void {
    let period_id: string | undefined;
    if (typ === 0) period_id = this.yearPeriod?.id;
    else if (typ === 1) period_id = this.monthPeriod?.id;
    else if (typ === 2) period_id = this.dayPeriod?.id;

    const idToClose = period_id;
    if (!idToClose || this.spin.isLoading) return;
    if (typ !== 2) {
      this.confirmClose(typ, idToClose, { text: CLOSE_WARNING });
      return;
    }

    // Récapitulatif et coffre lus sous le voile ; la boîte (ou, récapitulatif illisible, la confirmation d'origine) part du finalize posé APRÈS block, voile tombé.
    // Solde illisible : la boîte s'ouvre quand même, sans versement ; le Z ne doit jamais être bloqué.
    let data: ZCloseDialogData | null = null;
    let html: string | null = null;
    // Écran périmé (autre onglet, autre appareil) : la session est déjà close, la boîte proposerait un versement hors de toute session.
    let alreadyClosed = false;
    // Poste sans tiroir-caisse : ni solde ni boîte, la confirmation d'origine montre l'encaissé sans lignes de tiroir et le Z part sans cash_out.
    const hasDrawer = this.hasDrawer;
    forkJoin({
      recap: this.reglementService.getRecap$(idToClose),
      balance: hasDrawer ? this.reglementService.getBalance$().pipe(
        map((b): { b: StationBalance | null; error: string } => ({ b, error: '' })),
        catchError((e) => of({ b: null, error: refusalText(e) })),
      ) : of({ b: null, error: '' }),
    }).pipe(
      this.spin.block('Récapitulatif de la journée…'),
      finalize(() => {
        if (alreadyClosed) this.load('Relecture des périodes…', () => this.alertService.warn('Cette session est déjà clôturée (autre onglet ou autre poste) : écran relu.'));
        else if (data) this.openZDialog(data);
        else if (html !== null) this.confirmClose(typ, idToClose, { html, width: 620 });
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: ({ recap, balance }) => {
        if (recap.status !== 'open') alreadyClosed = true;
        else if (!hasDrawer) html = CLOSE_WARNING_HTML + recapBodyHtml(recap, this.cashFamily, false);
        else data = { periodId: idToClose, recap, balance: balance.b, balanceError: balance.error, family: this.cashFamily };
      },
      error: (e) => html = `${CLOSE_WARNING_HTML}<p style="margin:0;color:#92400e">Récapitulatif indisponible : ${esc(refusalText(e))}</p>`,
    });
  }

  // beforeClosed, pas afterClosed : émis dans close(), avant la chute du voile de la boîte, la relecture le relève sans trou.
  private openZDialog(data: ZCloseDialogData): void {
    // closeOnNavigation : le Retour du navigateur détruirait la boîte et couperait un versement ou un Z peut-être enregistré, sans rien dire.
    const ref = this.dialog.open<ZCloseDialogComponent, ZCloseDialogData, ZCloseResult>(ZCloseDialogComponent, { data, maxWidth: '96vw', autoFocus: 'dialog', closeOnNavigation: false });
    ref.beforeClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      if (!res?.closed) return;
      const drop = res.drop > 0 ? ` Versé au coffre : ${eur(res.drop)}.` : '';
      const text = res.warning ? `La période a été clôturée.${drop} ${res.warning}` : `La période a été clôturée avec succès.${drop} Fond de caisse laissé pour demain : ${eur(res.counted)}.`;
      this.load(CLOSE_MSG[2], () => Swal.fire('Clôturée !', text, res.warning ? 'warning' : 'success'));
    });
  }

  // Ligne « Espèces attendues » : lecture sans voile, un échec retire la ligne sans rien dire ; poste sans tiroir-caisse, ni lecture ni ligne.
  private loadExpectedCash(): void {
    const id = this.hasDrawer ? this.dayPeriod?.id : undefined;
    this.expectedCashSub?.unsubscribe();
    if (id !== this.expectedCashFor) this.expectedCash = null;
    this.expectedCashFor = id;
    if (!id) return;
    this.expectedCashSub = this.reglementService.getRecap$(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => this.expectedCash = r.expected_cash,
      error: () => this.expectedCash = null,
    });
  }

  /** Récapitulatif de la session journalière en lecture seule : mêmes règles de voile que le Z, sans bouton de clôture. */
  onShowRecap(): void {
    const id = this.dayPeriod?.id;
    if (!id || this.spin.isLoading) return;
    let swal: SweetAlertOptions | null = null;
    this.reglementService.getRecap$(id).pipe(
      this.spin.block('Récapitulatif de la journée…'),
      finalize(() => { if (swal) Swal.fire(swal); }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (recap) => {
        if (this.hasDrawer && recap.period_id === this.dayPeriod?.id) this.expectedCash = recap.expected_cash;
        swal = { title: 'Récapitulatif de la journée', html: sessionRecapHtml(recap, this.cashFamily, this.hasDrawer), icon: 'info', width: 620, confirmButtonText: 'Fermer' };
      },
      error: (e) => swal = { title: 'Récapitulatif indisponible', text: refusalText(e), icon: 'error', confirmButtonText: 'Fermer' },
    });
  }

  private confirmClose(typ: number, idToClose: string, body: { text?: string; html?: string; width?: number }): void {
    Swal.fire({
      title: 'Confirmation',
      ...body,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, la clôturer !',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.salesService.postPeriodClose(idToClose).pipe(this.spin.block(closeMsg(typ, this.hasDrawer))).subscribe({
          next: (response: ApiResponse<any>) => {
            console.log("[onClose] Server response: " + JSON.stringify(response));
            this.load(closeMsg(typ, this.hasDrawer), () => Swal.fire('Clôturée !', 'La période a été clôturée avec succès.', 'success'));
          },
          error: (error: HttpErrorResponse) => {
            console.error(error);
            this.alertService.error(error.message + "\n" + error.error);
          }
        });
      }
    });
  }

  /**
   * Requests the server to open a specific period.
   * Prompts for initial cash float for a daily session (typ = 2) on a station with a cash drawer; elsewhere the day opens without cash_in.
   *
   * @param typ The type identifier of the period to open (0 = Year, 1 = Month, 2 = Day).
   */
  onOpenPeriod(typ: number): void {
    if (this.spin.isLoading) return;
    if (typ === 2 && this.hasDrawer) {
      // Dernier Z et attendu du matin lus sous le voile, Swal voile tombé ; une lecture en échec laisse le champ vide, sans rien bloquer.
      let settled = false;
      let last: ClosingFloat | null = null;
      this.reglementService.getClosingFloat$().pipe(
        this.spin.block('Lecture du fond de caisse…'),
        finalize(() => { if (settled) this.askOpeningFloat(typ, last); }),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe({
        next: (r) => { last = r; settled = true; },
        error: (e) => { console.error('[PeriodsComponent] fond de la dernière clôture illisible', e); settled = true; },
      });
    } else {
      this._executeOpenPeriod(typ);
    }
  }

  // Mise en service : grille dans une boîte, son total remplace le fond saisi ; rouvrir reprend le dernier comptage.
  onCountEnrollFloat(): void {
    openCashCount(this.dialog, { title: 'Comptage du fond de caisse', counts: this.openingCounts })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => { if (!r) return; this.openingCounts = r.counts; this.enrollCountTotal = r.total; this.cashInControl.setValue(r.total); this.cashInControl.markAsTouched(); });
  }

  // Fond de mise en service retouché à la main après « Reporter » : signalé, jamais corrigé (même règle qu'au Z).
  get enrollDiffersFromCount(): boolean {
    return this.enrollCountTotal !== null && this.cashInControl.valid && this.cashInControl.value !== null && Number(this.cashInControl.value) !== this.enrollCountTotal;
  }

  private askOpeningFloat(typ: number, last: ClosingFloat | null): void {
    const float = last?.closing_float ?? null;
    // Proposé : l'attendu du serveur (compté au Z ± mouvements de nuit), sinon valider sans recompter crée un faux écart de nuit puis l'inverse au Z.
    const expected = last?.opening_expected ?? null;
    const night = last ? nightTransfers(last) : 0;
    const when = last?.closed_at ? `à la clôture du ${at(last.closed_at)}` : 'à la dernière clôture';
    const moved = night && expected !== null ? `, mouvements depuis : ${night > 0 ? '+' : ''}${eur(night)}, attendu : ${eur(expected)}` : '';
    const data: OpenFloatData = {
      label: float !== null ? `Fond laissé ${when} : ${eur(float)}${moved}` : null,
      // Un fond nul, négatif ou inconnu laisse le champ vide (0 proposé n'aide pas).
      proposed: expected !== null && expected > 0 ? expected : null,
    };
    // Boîte et non Swal : la grille de comptage pièces et billets y est ouverte d'emblée (JP 06/10).
    // Fermée par la navigation (elle ne poste rien elle-même) ; seul un nombre ouvre la journée, toute autre sortie n'envoie rien.
    this.dialog.open<OpenFloatDialogComponent, OpenFloatData, number>(OpenFloatDialogComponent, { data, maxWidth: '96vw', autoFocus: false })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cashIn) => { if (typeof cashIn === 'number' && Number.isFinite(cashIn)) this._executeOpenPeriod(typ, cashIn); });
  }

  // Refus métier (fond invalide, coffre) ou fond non enregistré (msg d'une réponse ok) : montrés après la relecture, voile tombé.
  private openNotice(response: ApiResponse<any>): (() => void) | undefined {
    if (response?.success === false) return () => this.alertService.error(response.error_msg || response.msg || 'Ouverture refusée, sans message du serveur');
    if (response?.msg) return () => this.alertService.warn(response.msg!);
    return undefined;
  }

  /**
   * Executes the API call to open the period.
   */
  private _executeOpenPeriod(typ: number, cashIn?: number): void {
    this.salesService.postPeriodOpen(typ, cashIn).pipe(this.spin.block(openMsg(typ, this.hasDrawer))).subscribe({
      next: (response: ApiResponse<any>) => {
        console.log("[onOpen] Server response: " + JSON.stringify(response));
        this.load(openMsg(typ, this.hasDrawer), this.openNotice(response));
      },
      error: (error: HttpErrorResponse) => {
        console.error(error);
        this.alertService.error(error.message + "\n" + error.error);
      }
    });
  }
}
